import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";

export const useDeleteSeason = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const request = useApi();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<DefaultOperationSuccessResponse, Error, string>({
    mutationFn: async (seasonId: string) => {
      const url = `${sport}/o/${organizationId}/seasons/${seasonId}`;
      return await request(url, {
        method: "DELETE",
      });
    },
    onSuccess: async (response, seasonId) => {
      const operationResult = response?.data?.[0];

      if (!operationResult?.success) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        updatedScope.scope.leagueTree = updatedScope.scope.leagueTree.map((league) => ({
          ...league,
          competitions: league.competitions.map((competition) => ({
            ...competition,
            seasonIds: competition.seasonIds.filter((id) => id !== seasonId),
          })),
        }));

        if (updatedScope.resources?.seasons?.[seasonId]) {
          delete updatedScope.resources.seasons[seasonId];
        }
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["seasons", sport, organizationId, seasonId] })]);
    },
  });
};
